/* eslint-disable @typescript-eslint/no-explicit-any */
import '../renderer/desktop.global';
import { browserWorkstation } from './browser-workstation';
import type { CapturePointDesktopApi, FieldEditUndoRequest } from '../shared/desktop-api';
import { thinCommands, type EngineCall } from './desktop-commands';
import { folderProgress } from './process-runs';
import { lookupCommands } from './lookup-commands';
import { destinationBinding, setupCommands } from './setup-commands';
import { filingCommands } from './filing-commands';
import { flaggedCommands } from './flagged-after-export';
import { engineCall } from './engine-call';
import { browserCaptures, type BrowserCaptures } from './browser-captures';
import { accentBridge, applyPaigeTheme } from './paige-theme';
import { changePersonAppearance, onPersonAppearance, personAppearance } from './person-appearance';
import type { PaigeWorkbenchBridge } from './paige.global';
import { intakeSources } from './intake-sources';
import { installPaigeHost } from './paige-host';
import { folderAnalysis } from './folder-analysis';
import { jobChannels } from './job-channels';
import { jobCreation } from './job-creation';
import { jobSetupFiles } from './job-setup-files';
import { refusalFor } from './not-carried';
import { pageAddresses } from './page-address';
import { batchReprocess } from './batch-reprocess';
import { batchRereadRuns } from './batch-reread-runs';
import { breakSheetCommands } from './break-sheet-commands';
import { desktopOnlyAnswers } from './desktop-only';
import { apiRootOf, engagementChannel } from './engagement-channel';
import { isJobChannel } from './job-channel';
import { serverEmailAsks } from './server-email-asks';
import { sampleTour } from './sample-tour';
import { browserAppLinks } from './app-links';
import { guardConfirmSave } from './confirm-save';
import { contentCentralStart } from './content-central-start';

export interface DesktopAdapterOptions {
  /** Where `/engine` and `/pages` answer: the engine host's origin, or the API's `/v1/jobs/{id}` channel. */
  engineOrigin: string;
  /** The tenant to act in, when the channel is the API's (the `Paige-Tenant` header; a query for images). */
  tenant?: string;
  /** The API's root (`/v1`) when a job the workbench creates must become a Paige job too. */
  apiOrigin?: string;
  /** What Paige hands the ported screens (window.paigeWorkbench); installed before the App renders. */
  bridge?: PaigeWorkbenchBridge;
}

/**
 * `window.capturePointDesktop` for the browser: the desktop's IPC bridge replaced by HTTP calls to the
 * engine host (the same `EngineRequest` the desktop main process sent over stdin), plus browser-native
 * answers for what was Electron's (appearance, zoom, windows, updates, licensing, cluster). A method
 * the adapter does not know yet resolves to undefined and says so once in the console, so the screen
 * comes up and the gaps are visible rather than fatal (docs/engineering/workbench-port.md).
 */
export function installDesktopAdapter(options: DesktopAdapterOptions): void {
  if (options.bridge) window.paigeWorkbench = options.bridge;
  installPaigeHost();
  const known = desktopAdapterMethods(options);
  applyPaigeTheme(currentTheme());
  window.capturePointDesktop = new Proxy(known, {
    get: (target, property) => resolveMethod(target, property),
  }) as unknown as CapturePointDesktopApi;
}

/**
 * Every bridge method the adapter answers, built but not installed. Exported so
 * `adapter-coverage.test.ts` can hold this set to what the ported renderer actually calls: a
 * method missing from here and from `not-carried.ts` resolves to `undefined`, which a screen that
 * asked for a change reads as the change having been made.
 */
export function desktopAdapterMethods(options: DesktopAdapterOptions): Record<string, unknown> {
  const { engineOrigin, tenant, apiOrigin } = options;
  const call = engineCall(engineOrigin, tenant);
  const channels = jobChannels(engineOrigin, tenant, apiOrigin);
  const captures = browserCaptures(call, channels);
  const known: Record<string, unknown> = {
    ...thinCommands(call),
    ...lookupCommands(call),
    ...setupCommands(call),
    ...filingCommands(call),
    ...flaggedCommands(call),
    ...destinationBinding(call, Boolean(apiOrigin) && engineOrigin === apiOrigin),
    ...browserShell(engineOrigin, tenant, captures.stageFiles),
    ...folderAnalysis(channels, captures.stageWaves),
    ...browserSession(),
    ...engineRequests(call),
    ...jobCreation(call, { engineOrigin, apiOrigin, tenant }, channels.remember),
    ...jobSetupFiles(call, { apiOrigin, tenant }, channels.remember),
    ...captures.methods,
    ...batchRereads(call),
    ...batchReprocess(call),
    ...breakSheetCommands(channels.channelFor, { engineOrigin, tenant }),
    ...learningControls(call),
    ...engineCorrections(call),
    ...accentBridge(currentTheme),
    ...browserTooltips(),
    ...browserSettings(),
    ...browserWorkstation(call),
    ...desktopOnlyAnswers(apiRootOf(engineOrigin, apiOrigin), tenant),
    ...emailAsks(options, call),
    ...browserAppLinks(),
    ...contentCentralStart(apiRootOf(engineOrigin, apiOrigin), tenant, channels.remember),
    ...sampleTour(
      { apiOrigin: apiRootOf(engineOrigin, apiOrigin), tenant },
      captures.stageFiles,
      channels.remember,
    ),
  };
  return known;
}

/** The learning note and the shortcut sheet, on Paige's API; a job's route turns its own note on and off. */
function emailAsks(options: DesktopAdapterOptions, call: EngineCall): Record<string, unknown> {
  const { engineOrigin, tenant, apiOrigin } = options;
  const api = apiRootOf(engineOrigin, apiOrigin);
  return serverEmailAsks({
    api,
    ...(tenant ? { tenant } : {}),
    ...(isJobChannel(engineOrigin) ? { jobApi: engineOrigin } : {}),
    engagement: engagementChannel(api, tenant),
    call,
  });
}

const reported = new Set<string>();
const unsubscribe = (): (() => void) => () => undefined;

/**
 * A known method, the appearance property, the refusal of a change Paige does not carry
 * (not-carried.ts), or a once-reported stand-in that resolves to undefined. The refusal matters:
 * resolving to undefined tells the screen the change went through, and it then removes the thing
 * from its own list and says so, when nothing happened at all.
 */
export function resolveMethod(target: Record<string, unknown>, property: string | symbol): unknown {
  if (typeof property !== 'string' || property === 'then') return undefined;
  if (property in target) return target[property];
  if (property === 'initialAppearance') return currentTheme();
  const refusal = refusalFor(property);
  if (refusal) return () => Promise.reject(new Error(refusal));
  const subscription = /^on[A-Z]/.test(property);
  return () => {
    // One line, once per method, no arguments: a method the adapter has not met is worth a word to a
    // developer, never a dump of what the screen passed (desktop-only.ts answers the expected ones).
    if (!reported.has(property)) {
      reported.add(property);
      console.warn(`[capturepoint adapter] ${property} is not implemented in the browser yet`);
    }
    // A subscription hands back its unsubscribe; everything else answers asynchronously.
    return subscription ? unsubscribe() : Promise.resolve(undefined);
  };
}

function currentTheme(): 'light' | 'dark' {
  const explicit = document.documentElement.dataset['theme'];
  if (explicit === 'light' || explicit === 'dark') return explicit;
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}
/** What Electron's window owned (appearance, zoom, panels, clipboard, page images) and the scanner sources. */
function browserShell(
  engineOrigin: string,
  tenant: string | undefined,
  stageFiles: BrowserCaptures['stageFiles'],
): Record<string, unknown> {
  const pageAddress = pageAddresses(engineOrigin, tenant);
  const zoom = {
    level: 0,
    percent: 100,
    minimumPercent: 100,
    maximumPercent: 100,
    atMinimum: true,
    atMaximum: true,
  };
  return {
    getAppearance: async () => currentTheme(),
    // Appearance's Light and Dark are the account door's sun and moon: one stored preference,
    // Paige's own (the dashboard's profile menu writes the same one), so a reload keeps the choice.
    setAppearance: async (mode: 'light' | 'dark') => {
      document.documentElement.dataset['theme'] = mode;
      applyPaigeTheme(mode);
      window.paigeWorkbench?.person?.setTheme?.(mode);
      return mode;
    },
    onAppearanceChanged: unsubscribe,
    getInterfaceZoom: async () => zoom,
    changeInterfaceZoom: async () => zoom,
    onInterfaceZoomChanged: unsubscribe,
    onReviewPanelStateChanged: unsubscribe,
    // The setup sample's progress, as the host reports it (process-runs.ts); pause state arrives
    // through Paige's own job views, not this screen.
    onFolderAnalysisProgress: folderProgress.subscribe,
    onJobProcessingStateChanged: unsubscribe,
    getDetachedReviewPanels: async () => [],
    ...intakeSources(stageFiles),
    copyText: async (text: string) => navigator.clipboard.writeText(text),
    // Page images come from the engine host by the path the workspace recorded; an <img> src is enough.
    readPagePreview: async (previewPath: string) => pageAddress(previewPath),
  };
}

/** What the desktop's licensing, updates, cluster and schedulers owned: Paige has none of them. */
function browserSession(): Record<string, unknown> {
  return {
    getRuntimeInfo: async () => ({ appVersion: 'paige', isPackaged: true, platform: 'linux' }),
    getActivationState: async () => ({
      configured: false,
      activated: true,
      status: 'unconfigured',
      lastConfirmedAt: null,
      offlineSince: null,
      lastFailureDetail: null,
    }),
    getUpdateStatus: async () => ({ state: 'disabled', reason: 'Paige updates itself.' }),
    onUpdateStatusChanged: unsubscribe,
    getCluster: async () => ({
      thisComputerName: 'Paige',
      identityCode: null,
      cluster: null,
      nearby: [],
      asking: null,
      expiredRequest: null,
    }),
    onClusterChanged: unsubscribe,
    onSpeedCheckStateChanged: unsubscribe,
    getJobProcessingState: async (jobId: string) => ({
      jobId,
      pauseRequested: false,
      activeOperations: 0,
      revision: 0,
      status: 'ready',
    }),
    learningScheduleState: async () => ({ schedule: null, run: null, hold: null }),
    initializeLearningSchedule: async () => ({ schedule: null, run: null, hold: null }),
  };
}

/** The settings page's preferences and desktop-only facts: tooltips, speed checks, calibration, export connections. */
function browserTooltips(): Record<string, unknown> {
  // Show tooltips is the person's, on the server with their accent, and the dashboard follows it too
  // (person-appearance.ts; Dustin, 2026-10-20). Until 2026-10-20 it was this browser's.
  return {
    getTooltips: async () => personAppearance().tooltips,
    setTooltips: async (enabled: boolean) =>
      (await changePersonAppearance((was) => ({ ...was, tooltips: enabled }))).tooltips,
    onTooltipsChanged: (callback: (enabled: boolean) => void) =>
      onPersonAppearance((appearance) => callback(appearance.tooltips)),
  };
}

function browserSettings(): Record<string, unknown> {
  return {
    // Speed checks measured the desktop's computer; Paige's speed is the pools' (docs/status).
    getSpeedCheckState: async () => ({ kind: 'idle' }),
    getSpeedCheckAvailability: async () => ({ canRun: false, blockers: ['no-sample-documents'] }),
    getBenchmarkHistory: async () => [],
    getSpeedCheckDetails: async () => [],
    getPerformanceCalibration: async () => null,
    getSpeedCheckSchedule: async () => ({
      preferences: { enabled: false },
      windowLabel: '',
      lastCompletedAt: null,
      lastSkipReason: null,
    }),
    onMeasureJobProgress: unsubscribe,
    // Turn page's steps: the desktop passes them back over IPC; a call on Paige's channel answers once, at the
    // end, so the toolbar keeps its first words ("Turning the page") for the few seconds a turn takes.
    onTurnPageProgress: unsubscribe,
  };
}

/** The desktop's thicker handlers, minus the Electron-only guards (licensing, staged captures, deletion barrier). */
function engineRequests(call: EngineCall): Record<string, unknown> {
  const passThrough = (command: string) => (request: object) =>
    call(command, request as Record<string, unknown>);
  return {
    getEngineHealth: () => call('health'),
    getExportSettings: (jobId: string) => call('export-settings', { jobId }),
    getLatestBatch: (jobId: string) => call('latest-batch', { jobId }),
    getBatchDocumentIndex: passThrough('batch-document-index'),
    getBatchDocumentWindow: passThrough('batch-document-window'),
    getBatchDocument: passThrough('batch-document'),
    // A refused confirmation says what happened to the person's work, never the engine's sentence (confirm-save.ts).
    saveDocumentCorrection: guardConfirmSave(passThrough('save-document-correction')),
    saveDocumentCorrections: guardConfirmSave(passThrough('save-document-corrections')),
    // Ctrl+Z after a value was saved (CapturePoint ef0ff40e): the engine takes the edit back or refuses in
    // plain words. A live capture's values are staged, not stored, so there is nothing to take back.
    undoFieldEdit: (request: FieldEditUndoRequest) => {
      if (request.batchId.startsWith('live:'))
        return Promise.reject(
          new Error('This change cannot be undone while its pages are still being read.'),
        );
      return call('undo-field-edit', {
        jobId: request.jobId,
        batchId: request.batchId,
        documentId: request.documentId,
        fieldEditUndo: { fieldName: request.fieldName, expectedField: request.expectedField },
      });
    },
    saveBoundaryMutation: passThrough('save-boundary-mutation'),
    boundaryPagesLike: passThrough('boundary-pages-like'),
    searchPages: passThrough('search-pages'),
    getLearningCapability: (jobId: string) => call('learning-capability', { jobId }),
    applyLearningToWaitingWork: (jobId: string) =>
      call('apply-learning-to-waiting-work', { jobId }),
    // One field format with the engine now (CapturePoint 818aaa2e): the catalogue goes over as the engine gives it.
    listDataTables: (jobId: string) => call('list-data-tables', { jobId }),
  };
}

/**
 * The controls that change what a document is understood to be. Each of them was unbound, so the
 * adapter's stand-in resolved `undefined` and the screen read that as the change having been made:
 * confirming a type rechecked nothing, a split left both halves on the original type, and a
 * changed field set was "saved" over a batch that had not moved.
 */
function batchRereads(call: EngineCall): Record<string, unknown> {
  const passThrough = (command: string) => (request: object) =>
    call(command, request as Record<string, unknown>);
  return {
    // The four that re-read what the reviewer is looking at. Every one of them was a control
    // whose answer resolved `undefined`, which the screen read as success: confirming a type
    // rechecked nothing, a split left both halves on the original type, and a changed field set
    // was "saved" over a batch that had not moved.
    reclassifyBoundaryDocuments: passThrough('reclassify-boundary-documents'),
    ...batchRereadRuns(call),
    reextractPreparedDocument: (request: any) =>
      call('reextract-prepared-document', {
        jobId: request.jobId,
        document: request.document,
        documentTypes: [request.documentType],
      }),
    reclassifyPreparedDocument: (request: any) =>
      call('reclassify-prepared-document', {
        jobId: request.jobId,
        document: request.document,
        documentTypes: request.documentTypes,
      }),
  };
}

/**
 * Clearing what a job has learned, and waiting for what it is learning. Reset learning threw
 * inside its own dialog (`result.job` on `undefined`) although the host had the case all along.
 */
function learningControls(call: EngineCall): Record<string, unknown> {
  return {
    resetLearning: (jobId: string, request: object) =>
      call('reset-learning', { jobId, learningReset: request }),
    // The engine's own wait (up to 8 seconds) for the job's layout learning to land before a
    // reading takes its maps: the host rebuilds the layout index in the background as the desktop
    // does, so there is something to wait for (parity L-15). Both call sites treat a refusal as
    // "carry on", so a host that is away costs the reading nothing.
    waitForLayoutCatchUp: (jobId: string) => call('wait-layout-catch-up', { jobId }),
  };
}

/**
 * Job suggestions, shaped as the desktop shaped them. The three confirmation withdrawals went with
 * CapturePoint's single confirm (e5d08406): the screen has no partial confirm to take back, and the
 * engine keeps the commands.
 */
function engineCorrections(call: EngineCall): Record<string, unknown> {
  return {
    applyJobSuggestion: (request: any) =>
      call('apply-job-suggestion', {
        jobId: request.jobId,
        suggestionId: request.suggestionId,
        query: request.proposedName || undefined,
        documentIds: request.excludedDocumentIds?.length ? request.excludedDocumentIds : undefined,
        // A new-type card: the proposed fields the person kept; none adds the type with no fields (CapturePoint D10).
        fields: request.keptFieldNames?.length ? request.keptFieldNames : undefined,
      }),
    undoJobSuggestion: (request: any) =>
      call('undo-job-suggestion', { jobId: request.jobId, suggestionId: request.suggestionId }),
  };
}
